import { useState, useMemo, useEffect, type KeyboardEvent } from 'react';
import { Building2, ChevronDown, ChevronRight } from 'lucide-react';
import type { OrgUnit } from '../../types/prioritization/enterpriseIdentity';
import {
  buildOrgUnitTreeForCycleScope,
  filterOrgUnitsForCycleScope,
  resolveOrgScopeNodeId,
  type OrgUnitTreeNode,
} from '../../lib/orgUnitTree';

interface OrgUnitTreePickerProps {
  units: OrgUnit[];
  /** Workspace scope — required for canon 10P single-root org chart display. */
  workspaceId?: string | null;
  selectedId: string;
  onSelect: (id: string) => void;
  placeholder?: string;
  required?: boolean;
}

function TreeRow({
  node,
  depth,
  selectedId,
  onSelect,
  isLast,
  parentLines,
}: {
  node: OrgUnitTreeNode;
  depth: number;
  selectedId: string;
  onSelect: (id: string) => void;
  isLast: boolean;
  parentLines: boolean[];
}) {
  const [expanded, setExpanded] = useState(depth < 1);
  const hasChildren = node.children.length > 0;
  const isSelected = selectedId === node.unit.id;

  const rowSelect = () => onSelect(node.unit.id);
  const rowKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      rowSelect();
    }
  };

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={rowSelect}
        onKeyDown={rowKeyDown}
        className={`w-full flex items-center gap-1.5 px-2 py-1.5 text-sm text-left transition-colors cursor-pointer  outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 ${
          isSelected
            ? 'bg-primary-50 text-primary-700 font-medium dark:bg-[rgba(212,160,23,0.12)] dark:text-[var(--mustard)]'
            : 'text-[var(--text-pri)] hover:bg-[var(--surface)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]'
        }`}
      >
        {depth > 0 && (
          <span className="flex-shrink-0 flex items-center" style={{ width: depth * 16 }}>
            {parentLines.map((hasLine, i) => (
              <span
                key={i}
                className="inline-block w-4 flex-shrink-0"
                style={{ borderLeft: hasLine ? '1px solid #e2e8f0' : 'none', height: '100%' }}
              />
            ))}
          </span>
        )}

        <button
          type="button"
          onClick={e => { e.stopPropagation(); if (hasChildren) setExpanded(v => !v); }}
          className={`flex-shrink-0 w-4 h-4 flex items-center justify-center rounded ${
            hasChildren ? 'text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-dim)] dark:hover:text-[var(--text-sec)]' : 'cursor-default'
          }`}
          aria-label={hasChildren ? (expanded ? 'Collapse' : 'Expand') : undefined}
          tabIndex={-1}
        >
          {hasChildren ? (
            expanded
              ? <ChevronDown className="h-3 w-3" />
              : <ChevronRight className="h-3 w-3" />
          ) : (
            <span className="w-3" />
          )}
        </button>

        <Building2 className={`h-3.5 w-3.5 flex-shrink-0 ${isSelected ? 'text-primary-500 dark:text-[var(--mustard)]' : 'text-[var(--text-ter)] dark:text-[var(--text-dim)]'}`} />

        <span className="truncate">{node.unit.displayName}</span>

        {node.unit.type && (
          <span className="ml-auto flex-shrink-0 text-xs text-[var(--text-ter)] font-normal dark:text-[var(--text-dim)]">{node.unit.type}</span>
        )}
      </div>

      {expanded && hasChildren && (
        <div>
          {node.children.map((child, idx) => (
            <TreeRow
              key={child.unit.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              isLast={idx === node.children.length - 1}
              parentLines={[...parentLines, !isLast]}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function OrgUnitTreePicker({
  units,
  workspaceId,
  selectedId,
  onSelect,
  placeholder = 'Select a node on the org chart',
  required,
}: OrgUnitTreePickerProps) {
  const activeUnits = useMemo(() => units.filter((u) => u.active), [units]);

  const scopeUnits = useMemo(
    () => filterOrgUnitsForCycleScope(activeUnits, workspaceId),
    [activeUnits, workspaceId],
  );

  const tree = useMemo(
    () => buildOrgUnitTreeForCycleScope(activeUnits, workspaceId),
    [activeUnits, workspaceId],
  );

  const resolvedSelectedId = useMemo(
    () => resolveOrgScopeNodeId(selectedId, activeUnits),
    [selectedId, activeUnits],
  );

  const selectedUnit = useMemo(
    () => scopeUnits.find((u) => u.id === resolvedSelectedId),
    [scopeUnits, resolvedSelectedId],
  );

  useEffect(() => {
    if (selectedId && resolvedSelectedId !== selectedId) {
      onSelect(resolvedSelectedId);
    }
  }, [selectedId, resolvedSelectedId, onSelect]);

  if (scopeUnits.length === 0) {
    return (
      <div className="w-full px-3 py-2 border border-[var(--rule)] text-sm text-[var(--text-ter)] bg-[var(--surface)] dark:border-[var(--rule-bright)] dark:bg-[var(--bg)] dark:text-[var(--text-dim)] dark:rounded-none">
        No org units available
      </div>
    );
  }

  return (
    <div className="w-full border border-[var(--rule)] overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent bg-[var(--bg-raised)] dark:border-[var(--rule-bright)] dark:bg-[var(--bg)] dark:rounded-none">
      <div className={`px-3 py-2 border-b border-[var(--rule)] text-sm dark:border-[var(--rule)] ${selectedUnit ? 'text-[var(--text-pri)] dark:text-[var(--text-pri)]' : 'text-[var(--text-ter)] dark:text-[var(--text-dim)]'}`}>
        {selectedUnit
          ? <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-primary-500 dark:text-[var(--mustard)]" />{selectedUnit.displayName}{required && !selectedId && <span className="text-[var(--coral)] ml-1">*</span>}</span>
          : placeholder}
      </div>
      <div className="max-h-44 overflow-y-auto">
        {tree.map((node, idx) => (
          <TreeRow
            key={node.unit.id}
            node={node}
            depth={0}
            selectedId={resolvedSelectedId}
            onSelect={onSelect}
            isLast={idx === tree.length - 1}
            parentLines={[]}
          />
        ))}
      </div>
    </div>
  );
}
